<template>
  <div class="pdp-wrapper">
    <v-data-table
      :headers="headers"
      :items="flatObjectiveRows"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="pdp-table"
      :item-class="() => 'table-row'"
      item-key="rowKey"
    >
      <template v-slot:[`header.objectives`]="{ header }">
        <div class="header-with-action">
          <span class="header-text header-text--left">{{ header.text }}</span>
          <v-btn
            v-if="activePeriodForActions && canEditPeriod(activePeriodForActions) && activePeriodForActions.Period_ID"
            small
            icon
            class="header-action-btn"
            @click.stop="showAddObjectiveDialog(activePeriodForActions)"
          >
            <v-icon small class="primary--text">mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:[`header.type`]="{ header }">
        <span class="header-text header-text--center">{{ header.text }}</span>
      </template>
      <template v-slot:[`header.means`]="{ header }">
        <span class="header-text header-text--left">{{ header.text }}</span>
      </template>
      <template v-slot:[`header.status`]="{ header }">
        <span class="header-text header-text--center">{{ header.text }}</span>
      </template>
      <template v-slot:body="{ items }">
        <tbody
          v-for="row in items"
          :key="row.rowKey"
        >
          <tr
            :class="{
              'objective-row': true,
              'objective-row--hover': hoveredRowKey === row.rowKey,
              'objective-row--dragging': draggingRowKey === row.rowKey,
              'objective-row--drag-over': dragOverRowKey === row.rowKey
            }"
            :data-period-id="row.period && row.period.Period_ID ? row.period.Period_ID : ''"
            :data-objective-id="row.objective ? row.objective.Objective_ID : ''"
            @mouseenter="onObjectiveRowMouseEnter(row)"
            @mouseleave="hoveredRowKey = null"
            @dragover.prevent="onObjectiveDragOver($event, row)"
            @dragleave="dragOverRowKey = null"
            @drop="onObjectiveDrop($event, row)"
          >
            <td class="objectives-td">
              <div class="objectives-cell objectives-cell--row">
                <template v-if="!row.period.Period_ID">
                  <span class="grey--text caption">Create period first</span>
                </template>
                <template v-else-if="row.objective">
                  <v-icon
                    v-if="canEditPeriod(row.period)"
                    class="drag-handle mr-2 grey--text"
                    style="cursor: move;"
                    small
                    draggable="true"
                    @dragstart="onObjectiveDragStart($event, row)"
                    @dragend="onObjectiveDragEndClient"
                  >
                    mdi-drag
                  </v-icon>
                  <textarea
                    v-model="row.objective.Objective_Text"
                    :disabled="!canEditPeriod(row.period)"
                    :readonly="!canEditPeriod(row.period)"
                    rows="1"
                    placeholder="Objective"
                    class="objective-input"
                    @focus="rememberObjectiveText(row.objective)"
                    @input="resizeObjectiveTextarea($event.target)"
                    @blur="onObjectiveBlur(row.objective)"
                  ></textarea>
                  <div class="objectives-actions">
                    <v-btn
                      v-if="canEditPeriod(row.period)"
                      icon
                      x-small
                      class="delete-icon-btn"
                      @click="deleteObjective(row.objective, row.period)"
                    >
                      <v-icon class="action-delete-icon">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  <span class="grey--text caption">No objectives</span>
                </template>
              </div>
            </td>
            <td class="type-td">
              <v-select
                v-if="row.objective"
                v-model="row.objective.Type"
                :items="typeOptions.filter(t => t !== 'Type')"
                :disabled="!canEditPeriod(row.period)"
                append-icon=""
                dense
                flat
                hide-details
                placeholder="Type"
                class="type-select"
                @change="updateObjective(row.objective)"
              >
                <template v-slot:item="{ item }">
                  <v-chip
                    :color="item === 'Main' ? 'primary' : 'orange'"
                    small
                    text-color="white"
                  >
                    {{ item }}
                  </v-chip>
                </template>
                <template v-slot:selection="{ item }">
                  <div :class="['dropdown-pill', getTypePillClass(item)]">
                    <span class="dropdown-pill__text">{{ item || 'Type' }}</span>
                    <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
                  </div>
                </template>
              </v-select>
              <span v-else class="grey--text caption">—</span>
            </td>
            <td class="means-td">
              <template v-if="row.objective">
                <div
                  v-if="canEditLimitedFields(row.period) && editingMeansIds.includes(row.objective.Objective_ID)"
                  class="means-edit-wrapper"
                >
                  <div class="means-edit-row">
                    <div
                      :ref="'meansHybrid-' + row.objective.Objective_ID"
                      class="means-hybrid"
                      :data-objective-id="String(row.objective.Objective_ID)"
                    >
                      <textarea
                        :value="meansCombined[row.objective.Objective_ID] || ''"
                        class="means-combined-textarea means-hybrid-textarea"
                        rows="1"
                        placeholder="Describe means…"
                        @input="onMeansCombinedInput(row.objective.Objective_ID, $event)"
                        @blur="onMeansBlur(row.objective, $event)"
                        @keydown.enter.exact.prevent="onMeansBlur(row.objective)"
                      />
                      <div
                        v-if="sortedMeansLinks(row.objective.Objective_ID).length"
                        class="means-links-row"
                        @mousedown.prevent
                      >
                        <v-chip
                          v-for="(lnk, linkIdx) in sortedMeansLinks(row.objective.Objective_ID)"
                          :key="'ml-' + row.objective.Objective_ID + '-' + linkIdx + '-' + lnk.offset"
                          small
                          outlined
                          color="primary"
                          class="means-hybrid-link-chip"
                          close
                          @click:close="removeMeansLinkAt(row.objective.Objective_ID, linkIdx)"
                        >
                          <span class="means-hybrid-link-label" :title="lnk.url">{{ lnk.label }}</span>
                          <v-btn
                            icon
                            x-small
                            class="means-hybrid-link-edit"
                            aria-label="Edit link"
                            @click.stop="openMeansLinkDialog(row.objective.Objective_ID, linkIdx)"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </v-chip>
                      </div>
                      <p class="means-hybrid-hint caption text--secondary mb-0">
                        One field for all text (link words are part of the sentence). Chips below edit or remove links. Select words in the field, then use the chain icon to add a link.
                      </p>
                    </div>
                    <v-btn
                      icon
                      x-small
                      class="means-link-btn"
                      title="Add link"
                      @mousedown.prevent
                      @click.stop="openMeansLinkDialog(row.objective.Objective_ID)"
                    >
                      <v-icon class="action-pencil-icon">mdi-link-variant</v-icon>
                    </v-btn>
                  </div>
                </div>
                <span v-else class="means-link-wrapper">
                  <span class="means-value">
                    <span
                      class="means-text"
                      v-html="formatMeansText(row.objective.Means)"
                    ></span>
                  </span>
                  <v-icon
                    v-if="canEditLimitedFields(row.period)"
                    class="means-edit-icon action-pencil-icon"
                    @click="startMeansEdit(row.objective)"
                  >mdi-pencil</v-icon>
                </span>
              </template>
              <span v-else class="grey--text caption">—</span>
            </td>
            <td class="status-td">
              <v-select
                v-if="row.objective"
                v-model="row.objective.Status"
                :items="statusOptions.filter(s => s !== 'Status')"
                :disabled="!canEditLimitedFields(row.period)"
                append-icon=""
                dense
                flat
                hide-details
                placeholder="Status"
                class="status-select"
                @change="updateObjective(row.objective)"
              >
                <template v-slot:item="{ item }">
                  <v-chip
                    :color="getStatusColor(item)"
                    small
                    text-color="white"
                  >
                    {{ item }}
                  </v-chip>
                </template>
                <template v-slot:selection="{ item }">
                  <div :class="['dropdown-pill', getStatusPillClass(item)]">
                    <span class="dropdown-pill__text">{{ item || 'Status' }}</span>
                    <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
                  </div>
                </template>
              </v-select>
              <span v-else class="grey--text caption">—</span>
            </td>
          </tr>
          <tr
            v-if="row.isLastObjective"
            class="period-notes-row"
          >
            <td :colspan="headers.length" class="period-notes-td">
              <div class="period-notes-label">
                <span>Notes</span>
                <v-btn
                  v-if="canEditLimitedFields(row.period) && row.period.Period_ID && addingNotePeriodId !== row.period.Period_ID"
                  small
                  icon
                  class="header-action-btn"
                  @click.stop="showNewNoteField(row.period)"
                >
                  <v-icon class="action-pencil-icon">mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div class="notes-cell">
                <div class="notes-content">
                  <div
                    v-if="getCurrentPeriodNote(row.period) && addingNotePeriodId !== row.period.Period_ID"
                    class="notes-list"
                  >
                    <span :key="getCurrentPeriodNote(row.period).Note_ID || row.period.Period_ID" class="note-comment">{{ getCurrentPeriodNote(row.period).Note_Text || '-' }}</span>
                  </div>
                  <v-textarea
                    v-if="canEditLimitedFields(row.period) && row.period.Period_ID && addingNotePeriodId === row.period.Period_ID"
                    :ref="'noteInput-' + row.period.Period_ID"
                    :value="noteDrafts[row.period.Period_ID] || ''"
                    placeholder="Note"
                    rows="1"
                    auto-grow
                    dense
                    flat
                    solo
                    hide-details
                    class="notes-inline-input"
                    @input="setNoteDraft(row.period.Period_ID, $event)"
                    @blur="onNoteBlur(row.period)"
                    @keydown.enter.exact.prevent="onNoteBlur(row.period)"
                  />
                  <div v-else-if="!getCurrentPeriodNote(row.period)" class="no-notes">-</div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <ConfirmOrDeniedDialog
      v-if="showDeleteObjectiveDialog"
      v-model="showDeleteObjectiveDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'Are you sure you want to delete this objective?'"
      @onDeleteClick="confirmDeleteObjective"
      @onCancelClick="showDeleteObjectiveDialog = false"
      @onCloseClick="showDeleteObjectiveDialog = false"
    />

    <v-dialog
      v-model="showMeansLinkDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">{{ meansLinkDialogIsEdit ? 'Edit link' : 'Add link' }}</v-card-title>
        <v-card-text class="pb-0">
          <p class="caption text--secondary mb-3">
            Paste the URL first. Link text is optional (defaults to the site name). To link words in a text box, select them, then click the chain icon. To edit a chip, use its pencil icon.
          </p>
          <v-text-field
            ref="meansLinkUrlInput"
            v-model="meansLinkUrl"
            label="URL"
            placeholder="https://example.com"
            outlined
            dense
            hide-details
            class="mb-3"
            @keydown.enter.prevent="applyMeansLink"
          />
          <v-text-field
            v-model="meansLinkText"
            label="Link text (optional)"
            placeholder="Short label readers will see"
            outlined
            dense
            hide-details
            @keydown.enter.prevent="focusMeansLinkUrlField"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeMeansLinkDialog">Cancel</v-btn>
          <v-btn color="primary" @click="applyMeansLink">{{ meansLinkDialogIsEdit ? 'Save' : 'Insert' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { escapeHtml, formatMeansText, normalizeLinkUrl } from '@/utils/linkifyText'
export default {
  name: 'PdpTab',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    },
    selectedMeeting: {
      required: false,
      type: Object,
      default: null
    }
  },
  data () {
    return {
      hoveredRowKey: null,
      editingMeansIds: [],
      meansDrafts: {},
      meansSaveLocks: {},
      objectiveTextOriginals: {},
      headers: [
        { text: 'Objectives', value: 'objectives', sortable: false, width: '300px', align: 'start' },
        { text: 'Type', value: 'type', sortable: false, width: '45px', align: 'center' },
        { text: 'Means', value: 'means', sortable: false, width: '270px', align: 'start' },
        { text: 'Status', value: 'status', sortable: false, width: '60px', align: 'center' }
      ],
      periods: [],
      objectives: {},
      notes: {},
      noteDrafts: {},
      noteSaveLocks: {},
      addingNotePeriodId: null,
      typeOptions: ['Type', 'Main', 'Additional'],
      statusOptions: ['Status', 'Done', 'In Progress', 'Not Done', 'Not Applicable', 'On Hold'],
      showDeleteObjectiveDialog: false,
      currentObjectiveToDelete: null,
      draggingRowKey: null,
      dragOverRowKey: null,
      showMeansLinkDialog: false,
      meansLinkUrl: '',
      meansLinkText: '',
      meansLinkObjectiveId: null,
      meansLinkEditSegmentIndex: null,
      meansCombined: {},
      meansLinksMeta: {},
      meansLinkTextSelectionStart: 0,
      meansLinkTextSelectionEnd: 0
    }
  },
  computed: {
    meansLinkDialogIsEdit () {
      return this.meansLinkEditSegmentIndex !== null && this.meansLinkEditSegmentIndex >= 0
    },
    user () {
      return this.$store.getters.getUser
    },
    isHRorFinancierOrManager () {
      return this.rolesOfUser.some(role => role.RoleName === 'HR' || role.RoleName === 'financier' || role.RoleName === 'manager')
    },
    isCurrentEmployee () {
      return this.employee && this.user && this.employee.Employee_ID === this.user.employee_id
    },
    isSupervisorOfEmployee () {
      return this.employee && this.user &&
        this.employee.Supervisor_ID === this.user.employee_id
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    periodsWithObjectives () {
      return this.periods.map(period => {
        const periodId = period.Period_ID
        const periodObjectives = periodId ? (this.objectives[periodId] || []) : []
        return {
          ...period,
          objectives: periodObjectives
        }
      })
    },
    filteredPeriodsWithObjectives () {
      if (!this.selectedMeeting || !this.selectedMeeting.Meeting_ID) {
        return this.periodsWithObjectives
      }
      const selectedMeetingId = String(this.selectedMeeting.Meeting_ID)
      return this.periodsWithObjectives.filter(period => {
        const periodMeetingId = period.Meeting_ID || period.Meeting_ID_From_Meeting
        return String(periodMeetingId) === selectedMeetingId
      })
    },
    activePeriodForActions () {
      return this.filteredPeriodsWithObjectives.length ? this.filteredPeriodsWithObjectives[0] : null
    },
    flatObjectiveRows () {
      const rows = []
      for (const period of this.filteredPeriodsWithObjectives) {
        if (!period.Period_ID) {
          rows.push({
            rowKey: `period-${period.Meeting_ID || 'noperiod'}-no-period`,
            period,
            objective: null,
            isLastObjective: true
          })
          continue
        }
        const objectives = period.objectives || []
        if (objectives.length === 0) {
          rows.push({
            rowKey: `period-${period.Period_ID}-empty`,
            period,
            objective: null,
            isLastObjective: true
          })
        } else {
          objectives.forEach((objective, index) => {
            rows.push({
              rowKey: `period-${period.Period_ID}-obj-${objective.Objective_ID}`,
              period,
              objective,
              isLastObjective: index === objectives.length - 1
            })
          })
        }
      }
      return rows
    }
  },
  watch: {
    flatObjectiveRows () {
      this.scheduleObjectiveTextareaResize()
    }
  },
  async mounted () {
    this.objectiveCellWidths = new WeakMap()
    this.objectiveTextareaResizeObserver = new ResizeObserver(entries => {
      let widthChanged = false
      for (const entry of entries) {
        const width = entry.contentRect.width
        const last = this.objectiveCellWidths.get(entry.target)
        if (last === width) continue
        this.objectiveCellWidths.set(entry.target, width)
        widthChanged = true
      }
      if (widthChanged) this.resizeAllObjectiveTextareas()
    })
    window.addEventListener('resize', this.scheduleObjectiveTextareaResize)
    if (this.employee && this.employee.Employee_ID) {
      await this.loadPeriods()
    }
    this.scheduleObjectiveTextareaResize()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.scheduleObjectiveTextareaResize)
    if (this.objectiveTextareaResizeObserver) {
      this.objectiveTextareaResizeObserver.disconnect()
      this.objectiveTextareaResizeObserver = null
    }
  },
  methods: {
    emitPdpPeriodMetadataChanged () {
      this.$emit('pdp-period-metadata-changed')
    },
    isLink (value) {
      if (!value) return false
      try {
        const url = new URL(value)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch {
        return false
      }
    },
    setMeansDraft (objectiveId, value) {
      this.$set(this.meansDrafts, objectiveId, value)
    },
    mergeAdjacentTextSegments (segments) {
      const out = []
      for (const seg of segments) {
        if (!seg) continue
        if (seg.type === 'text' && out.length && out[out.length - 1].type === 'text') {
          const prev = out[out.length - 1]
          out[out.length - 1] = { type: 'text', text: (prev.text || '') + (seg.text || '') }
        } else if (seg.type === 'link') {
          out.push({ type: 'link', label: seg.label, url: seg.url })
        } else {
          out.push({ type: 'text', text: seg.text || '' })
        }
      }
      return out.length ? out : [{ type: 'text', text: '' }]
    },
    parseMeansMarkdown (raw) {
      const s = String(raw || '')
      const segments = []
      const re = /\[([^\]]+)\]\(([^)\s]+)\)/g
      let lastIndex = 0
      let m
      while ((m = re.exec(s)) !== null) {
        if (m.index > lastIndex) {
          segments.push({ type: 'text', text: s.slice(lastIndex, m.index) })
        }
        segments.push({ type: 'link', label: m[1], url: m[2] })
        lastIndex = re.lastIndex
      }
      if (lastIndex < s.length) {
        segments.push({ type: 'text', text: s.slice(lastIndex) })
      }
      if (segments.length === 0) {
        segments.push({ type: 'text', text: '' })
      }
      const merged = this.mergeAdjacentTextSegments(segments)
      if (merged.length && merged[merged.length - 1].type === 'link') {
        merged.push({ type: 'text', text: '' })
      }
      return this.mergeAdjacentTextSegments(merged)
    },
    segmentsToCombinedFromSegments (segs) {
      let combined = ''
      const links = []
      for (const s of segs) {
        if (s.type === 'text') combined += (s.text || '')
        else if (s.type === 'link') {
          const lab = s.label || ''
          links.push({ offset: combined.length, label: lab, url: s.url })
          combined += lab
        }
      }
      return { combined, links }
    },
    combinedTextAndLinksToMarkdown (combined, links) {
      const sorted = [...links].sort((a, b) => a.offset - b.offset)
      let pos = 0
      let out = ''
      for (const l of sorted) {
        const off = Math.max(0, Math.min(l.offset, combined.length))
        out += combined.slice(pos, off)
        const u = this.normalizeLinkUrl(l.url)
        if (!u) continue
        const lab = this.sanitizeMarkdownLinkLabel(l.label || '')
        out += `[${lab || 'link'}](${u})`
        pos = off + (l.label || '').length
        if (pos > combined.length) pos = combined.length
      }
      out += combined.slice(pos)
      return out
    },
    syncMeansCombinedFromMarkdown (objectiveId) {
      const segs = this.parseMeansMarkdown(this.meansDrafts[objectiveId] || '')
      const { combined, links } = this.segmentsToCombinedFromSegments(segs)
      this.$set(this.meansCombined, objectiveId, combined)
      this.$set(this.meansLinksMeta, objectiveId, links)
    },
    adjustMeansLinkOffsetsForTextChange (oldStr, newStr, links) {
      let i = 0
      const n = Math.min(oldStr.length, newStr.length)
      while (i < n && oldStr[i] === newStr[i]) i++
      const delta = newStr.length - oldStr.length
      for (const l of links) {
        if (l.offset >= i) l.offset += delta
        if (l.offset < 0) l.offset = 0
        if (l.offset > newStr.length) l.offset = newStr.length
      }
    },
    sortedMeansLinks (objectiveId) {
      const arr = this.meansLinksMeta[objectiveId]
      if (!arr || !arr.length) return []
      return [...arr].sort((a, b) => a.offset - b.offset)
    },
    resizeMeansCombinedTextarea (el) {
      if (!el || !el.style) return
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    },
    resizeObjectiveTextarea (el) {
      if (!el || !el.style) return
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    },
    resizeAllObjectiveTextareas () {
      const root = this.$el
      if (!root || !root.querySelectorAll) return
      root.querySelectorAll('textarea.objective-input').forEach(el => {
        this.resizeObjectiveTextarea(el)
      })
    },
    observeObjectiveTextareas () {
      const observer = this.objectiveTextareaResizeObserver
      const root = this.$el
      if (!observer || !root || !root.querySelectorAll) return
      observer.disconnect()
      root.querySelectorAll('.objectives-cell--row').forEach(el => observer.observe(el))
    },
    scheduleObjectiveTextareaResize () {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.observeObjectiveTextareas()
          this.resizeAllObjectiveTextareas()
        })
      })
    },
    clearMeansLinkTextSelection () {
      this.meansLinkTextSelectionStart = 0
      this.meansLinkTextSelectionEnd = 0
    },
    getMeansHybridRoot (objectiveId) {
      const ref = this.$refs['meansHybrid-' + objectiveId]
      return Array.isArray(ref) ? ref[0] : ref
    },
    getMeansCombinedTextarea (objectiveId) {
      const root = this.getMeansHybridRoot(objectiveId)
      return root && root.querySelector ? root.querySelector('.means-combined-textarea') : null
    },
    onMeansCombinedInput (objectiveId, e) {
      const el = e && e.target
      const newC = el ? el.value : ''
      const oldC = this.meansCombined[objectiveId] || ''
      const links = (this.meansLinksMeta[objectiveId] || []).map(l => ({ ...l }))
      this.adjustMeansLinkOffsetsForTextChange(oldC, newC, links)
      const md = this.combinedTextAndLinksToMarkdown(newC, links)
      this.setMeansDraft(objectiveId, md)
      const segs = this.parseMeansMarkdown(md)
      const synced = this.segmentsToCombinedFromSegments(segs)
      this.$set(this.meansCombined, objectiveId, synced.combined)
      this.$set(this.meansLinksMeta, objectiveId, synced.links)
      this.$nextTick(() => {
        this.resizeMeansCombinedTextarea(this.getMeansCombinedTextarea(objectiveId))
      })
    },
    removeMeansLinkAt (objectiveId, linkIndex) {
      const links = this.sortedMeansLinks(objectiveId)
      const rm = links[linkIndex]
      if (!rm) return
      let c = this.meansCombined[objectiveId] || ''
      const labLen = (rm.label || '').length
      c = c.slice(0, rm.offset) + c.slice(rm.offset + labLen)
      const rest = links.filter((_, i) => i !== linkIndex)
      for (const l of rest) {
        if (l.offset > rm.offset) l.offset -= labLen
      }
      const md = this.combinedTextAndLinksToMarkdown(c, rest)
      this.$set(this.meansCombined, objectiveId, c)
      this.$set(this.meansLinksMeta, objectiveId, rest)
      this.setMeansDraft(objectiveId, md)
    },
    async onMeansBlur (objective, evt) {
      if (!objective || !objective.Objective_ID) return
      const objectiveId = objective.Objective_ID
      const rt = evt && evt.relatedTarget
      const hybrid = this.getMeansHybridRoot(objectiveId)
      const wrapper = hybrid && hybrid.closest ? hybrid.closest('.means-edit-wrapper') : null
      if (rt && wrapper && typeof rt.closest === 'function' && wrapper.contains(rt)) return
      if (this.showMeansLinkDialog && this.meansLinkObjectiveId === objectiveId) return
      if (this.meansSaveLocks[objectiveId]) return
      this.$set(this.meansSaveLocks, objectiveId, true)
      try {
        const idx = this.editingMeansIds.indexOf(objectiveId)
        if (idx !== -1) this.editingMeansIds.splice(idx, 1)
        const draft = this.meansDrafts[objectiveId]
        const nextValue = typeof draft === 'string' ? draft : ''
        if (nextValue === (objective.Means || '')) return
        objective.Means = nextValue
        await this.updateObjective(objective)
      } finally {
        this.$delete(this.meansCombined, objectiveId)
        this.$delete(this.meansLinksMeta, objectiveId)
        this.$set(this.meansSaveLocks, objectiveId, false)
      }
    },
    startMeansEdit (objective) {
      if (!objective || !objective.Objective_ID) return
      if (this.editingMeansIds.includes(objective.Objective_ID)) {
        this.syncMeansCombinedFromMarkdown(objective.Objective_ID)
        this.$nextTick(() => {
          const ta = this.getMeansCombinedTextarea(objective.Objective_ID)
          if (ta) ta.focus()
          this.resizeMeansCombinedTextarea(ta)
        })
        return
      }
      this.editingMeansIds.push(objective.Objective_ID)
      this.$set(this.meansDrafts, objective.Objective_ID, objective.Means || '')
      this.syncMeansCombinedFromMarkdown(objective.Objective_ID)
      this.$nextTick(() => {
        const ta = this.getMeansCombinedTextarea(objective.Objective_ID)
        if (ta) {
          ta.focus()
          const len = (ta.value || '').length
          ta.setSelectionRange(len, len)
        }
        this.resizeMeansCombinedTextarea(ta)
      })
    },
    openMeansLinkDialog (objectiveId, editLinkIndex) {
      this.syncMeansCombinedFromMarkdown(objectiveId)
      const sorted = this.sortedMeansLinks(objectiveId)
      const editIdx = typeof editLinkIndex === 'number' ? editLinkIndex : null
      if (editIdx !== null) {
        const l = sorted[editIdx]
        if (!l) return
        this.meansLinkEditSegmentIndex = editIdx
        this.clearMeansLinkTextSelection()
        this.meansLinkText = l.label || ''
        this.meansLinkUrl = l.url || ''
      } else {
        this.meansLinkEditSegmentIndex = null
        this.clearMeansLinkTextSelection()
        this.meansLinkText = ''
        this.meansLinkUrl = ''
        const active = typeof document !== 'undefined' ? document.activeElement : null
        const root = this.getMeansHybridRoot(objectiveId)
        if (
          active &&
          active.classList &&
          active.classList.contains('means-combined-textarea') &&
          root &&
          root.contains &&
          root.contains(active)
        ) {
          const lo = typeof active.selectionStart === 'number' ? active.selectionStart : 0
          const hi = typeof active.selectionEnd === 'number' ? active.selectionEnd : 0
          const a = Math.min(lo, hi)
          const b = Math.max(lo, hi)
          if (a !== b) {
            const c = this.meansCombined[objectiveId] || ''
            this.meansLinkTextSelectionStart = Math.max(0, Math.min(a, c.length))
            this.meansLinkTextSelectionEnd = Math.max(this.meansLinkTextSelectionStart, Math.min(b, c.length))
            this.meansLinkText = c.slice(this.meansLinkTextSelectionStart, this.meansLinkTextSelectionEnd)
          }
        }
      }
      this.meansLinkObjectiveId = objectiveId
      this.showMeansLinkDialog = true
      this.focusMeansLinkUrlField({ caretToStart: true })
    },
    closeMeansLinkDialog () {
      const objectiveId = this.meansLinkObjectiveId
      this.showMeansLinkDialog = false
      this.meansLinkUrl = ''
      this.meansLinkText = ''
      this.meansLinkObjectiveId = null
      this.meansLinkEditSegmentIndex = null
      this.clearMeansLinkTextSelection()
      this.$nextTick(() => {
        if (!objectiveId) return
        const ta = this.getMeansCombinedTextarea(objectiveId)
        if (ta) ta.focus()
        this.resizeMeansCombinedTextarea(ta)
      })
    },
    focusMeansLinkUrlField (opts) {
      this.$nextTick(() => {
        const inputRef = this.$refs.meansLinkUrlInput
        const inputComp = Array.isArray(inputRef) ? inputRef[0] : inputRef
        const inputEl = inputComp && inputComp.$el ? inputComp.$el.querySelector('input') : null
        if (!inputEl) return
        inputEl.focus()
        if (opts && opts.caretToStart) inputEl.setSelectionRange(0, 0)
      })
    },
    defaultLinkLabelFromUrl (normalizedUrl) {
      try {
        const u = new URL(normalizedUrl)
        const host = u.hostname.replace(/^www\./i, '')
        return host || 'Link'
      } catch (_) {
        return 'Link'
      }
    },
    sanitizeMarkdownLinkLabel (label) {
      return String(label || '').replace(/\]/g, '')
    },
    applyMeansLink () {
      const objectiveId = this.meansLinkObjectiveId
      if (!objectiveId) {
        this.closeMeansLinkDialog()
        return
      }
      const normalizedUrl = this.normalizeLinkUrl(this.meansLinkUrl)
      if (!normalizedUrl) {
        this.$store.dispatch('setError', { errorMsg: 'Please provide a valid http/https URL' })
        return
      }
      let linkLabel = this.sanitizeMarkdownLinkLabel(String(this.meansLinkText || '').trim())
      if (!linkLabel) {
        linkLabel = this.defaultLinkLabelFromUrl(normalizedUrl)
      }
      if (!linkLabel) linkLabel = 'Link'

      this.syncMeansCombinedFromMarkdown(objectiveId)
      let c = this.meansCombined[objectiveId] || ''
      let links = this.sortedMeansLinks(objectiveId).map(l => ({ ...l }))
      const editIdx = this.meansLinkEditSegmentIndex
      const hadTextSelection = this.meansLinkTextSelectionStart !== this.meansLinkTextSelectionEnd
      let caretAfter = null

      if (this.meansLinkDialogIsEdit && editIdx !== null && links[editIdx]) {
        const l = links[editIdx]
        const oldLen = (l.label || '').length
        c = c.slice(0, l.offset) + linkLabel + c.slice(l.offset + oldLen)
        const delta = linkLabel.length - oldLen
        l.label = linkLabel
        l.url = normalizedUrl
        for (let j = 0; j < links.length; j++) {
          if (j !== editIdx && links[j].offset > l.offset) links[j].offset += delta
        }
      } else if (hadTextSelection) {
        const lo = this.meansLinkTextSelectionStart
        const hi = this.meansLinkTextSelectionEnd
        const a = Math.max(0, Math.min(lo, hi, c.length))
        const b = Math.max(a, Math.min(Math.max(lo, hi), c.length))
        const kept = links.filter((l) => {
          const end = l.offset + (l.label || '').length
          return end <= a || l.offset >= b
        })
        const delta = linkLabel.length - (b - a)
        for (const l of kept) {
          if (l.offset >= b) l.offset += delta
        }
        c = c.slice(0, a) + linkLabel + c.slice(b)
        kept.push({ offset: a, label: linkLabel, url: normalizedUrl })
        kept.sort((x, y) => x.offset - y.offset)
        links = kept
        caretAfter = a + linkLabel.length
      } else {
        if (c.length && !/\s$/.test(c)) c += ' '
        const off = c.length
        c += linkLabel
        links.push({ offset: off, label: linkLabel, url: normalizedUrl })
        caretAfter = c.length
      }

      const md = this.combinedTextAndLinksToMarkdown(c, links)
      const synced = this.segmentsToCombinedFromSegments(this.parseMeansMarkdown(md))
      this.$set(this.meansCombined, objectiveId, synced.combined)
      this.$set(this.meansLinksMeta, objectiveId, synced.links)
      this.setMeansDraft(objectiveId, md)
      this.showMeansLinkDialog = false
      this.meansLinkUrl = ''
      this.meansLinkText = ''
      this.meansLinkObjectiveId = null
      this.meansLinkEditSegmentIndex = null
      this.clearMeansLinkTextSelection()
      const pos = caretAfter
      this.$nextTick(() => {
        const ta = this.getMeansCombinedTextarea(objectiveId)
        if (ta) {
          ta.focus()
          if (pos !== null && pos >= 0) {
            const len = (ta.value || '').length
            const p = Math.min(pos, len)
            ta.setSelectionRange(p, p)
          }
        }
        this.resizeMeansCombinedTextarea(ta)
      })
    },
    escapeHtml,
    normalizeLinkUrl,
    formatMeansText,
    getDragPayload (event) {
      try {
        const raw = event.dataTransfer.getData('application/json')
        return raw ? JSON.parse(raw) : null
      } catch (_) {
        return null
      }
    },
    canDropObjective (source, row) {
      return row.objective && row.period &&
        String(source.periodId) === String(row.period.Period_ID) &&
        source.objectiveId !== row.objective.Objective_ID
    },
    onObjectiveDragStart (event, row) {
      if (!row.objective || !row.period || !row.period.Period_ID) return
      this.draggingRowKey = row.rowKey
      event.dataTransfer.setData('application/json', JSON.stringify({
        periodId: row.period.Period_ID,
        objectiveId: row.objective.Objective_ID
      }))
      event.dataTransfer.effectAllowed = 'move'
    },
    onObjectiveDragEndClient () {
      this.draggingRowKey = null
      this.dragOverRowKey = null
    },
    onObjectiveDragOver (event, row) {
      if (!this.draggingRowKey) return
      const source = this.getDragPayload(event)
      if (!source || !this.canDropObjective(source, row)) return
      event.dataTransfer.dropEffect = 'move'
      this.dragOverRowKey = row.rowKey
    },
    onObjectiveDrop (event, row) {
      this.draggingRowKey = null
      this.dragOverRowKey = null
      const source = this.getDragPayload(event)
      if (!source || !this.canDropObjective(source, row)) return
      event.preventDefault()
      const period = this.periodsWithObjectives.find(p => String(p.Period_ID) === String(source.periodId))
      if (!period || !period.objectives || !period.objectives.length) return
      const fromIdx = period.objectives.findIndex(o => o.Objective_ID === source.objectiveId)
      const toIdx = period.objectives.findIndex(o => o.Objective_ID === row.objective.Objective_ID)
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return
      const reordered = [...period.objectives]
      const [removed] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, removed)
      this.$set(this.objectives, period.Period_ID, reordered)
      this.saveObjectiveOrder(period, reordered)
    },
    async saveObjectiveOrder (period, reorderedObjectives) {
      const list = reorderedObjectives || period.objectives || []
      const headers = { Authorization: 'Bearer ' + this.user.token }
      try {
        for (let i = 0; i < list.length; i++) {
          const obj = list[i]
          if (obj.Order_Index === i) continue
          await this.axios.put(`/api/pdp/objectives/${obj.Objective_ID}`, {
            objectiveText: obj.Objective_Text,
            type: obj.Type,
            means: obj.Means,
            status: obj.Status,
            orderIndex: i
          }, { headers })
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Objective order saved successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (err) {
        this.$store.dispatch('setError', { errorMsg: 'Error updating objective order: ' + err.message })
        await this.loadObjectives(period.Period_ID)
      }
    },
    onObjectiveRowMouseEnter (row) {
      this.hoveredRowKey = row.rowKey
    },
    rememberObjectiveText (objective) {
      if (!objective || !objective.Objective_ID) return
      this.$set(this.objectiveTextOriginals, objective.Objective_ID, objective.Objective_Text || '')
    },
    async onObjectiveBlur (objective) {
      if (!objective || !objective.Objective_ID) return
      const objectiveId = objective.Objective_ID
      const originalText = Object.prototype.hasOwnProperty.call(this.objectiveTextOriginals, objectiveId)
        ? this.objectiveTextOriginals[objectiveId]
        : objective.Objective_Text
      const currentText = objective.Objective_Text || ''
      this.$delete(this.objectiveTextOriginals, objectiveId)
      if (currentText === originalText) return
      await this.updateObjective(objective)
    },
    canEditPeriod (period) {
      if (!period || !period.Meeting_Start_Time) return false
      return this.isHRorFinancierOrManager ||
        this.isSupervisorOfEmployee ||
        this.isCurrentEmployee
    },
    canEditLimitedFields (period) {
      if (!period || !period.Meeting_Start_Time) return false
      const isOwnData = period.Employee_ID === this.user.employee_id
      return this.isHRorFinancierOrManager || (isOwnData && this.isCurrentEmployee)
    },
    async loadPeriods () {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.periods = response.data

        for (const period of this.periods) {
          if (period.Period_ID) {
            await this.loadObjectives(period.Period_ID)
            await this.loadPeriodNotes(period.Period_ID)
          }
        }
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async loadObjectives (periodId) {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${periodId}/objectives`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$set(this.objectives, periodId, response.data)
      } catch (error) {
        this.$set(this.objectives, periodId, [])
      }
    },
    async loadPeriodNotes (periodId) {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${periodId}/notes`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$set(this.notes, periodId, response.data)
      } catch (error) {
        this.$set(this.notes, periodId, [])
      }
    },
    getStatusColor (status) {
      const colors = {
        Done: 'green',
        'In Progress': 'amber',
        'Not Done': 'grey',
        'Not Applicable': 'purple',
        'On Hold': 'orange'
      }
      return colors[status] || 'grey'
    },
    getTypePillClass (type) {
      if (type === 'Main') return 'dropdown-pill--type-main'
      if (type === 'Additional') return 'dropdown-pill--type-additional'
      return 'dropdown-pill--type-empty'
    },
    getStatusPillClass (status) {
      const classMap = {
        Done: 'dropdown-pill--status-done',
        'In Progress': 'dropdown-pill--status-in-progress',
        'Not Done': 'dropdown-pill--status-not-done',
        'Not Applicable': 'dropdown-pill--status-not-applicable',
        'On Hold': 'dropdown-pill--status-on-hold'
      }
      return classMap[status] || 'dropdown-pill--status-empty'
    },
    getPeriodIdForObjective (objective) {
      if (!objective || !objective.Objective_ID) return null
      if (objective.Period_ID) return objective.Period_ID
      for (const [periodId, objectives] of Object.entries(this.objectives || {})) {
        const found = (objectives || []).some(obj => String(obj.Objective_ID) === String(objective.Objective_ID))
        if (found) return Number(periodId)
      }
      return null
    },
    async updateObjective (objective) {
      const periodId = this.getPeriodIdForObjective(objective)
      if (periodId && objective && objective.Objective_ID) {
        this.$emit('objective-updated', {
          periodId,
          objectiveId: objective.Objective_ID,
          objective: {
            Objective_ID: objective.Objective_ID,
            Period_ID: periodId,
            Status: objective.Status,
            Type: objective.Type
          }
        })
      }
      try {
        await this.axios.put(`/api/pdp/objectives/${objective.Objective_ID}`, {
          objectiveText: objective.Objective_Text,
          type: objective.Type,
          means: objective.Means,
          status: objective.Status,
          orderIndex: objective.Order_Index || 0
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.$store.dispatch('setSuccess', { successMsg: 'Objective updated successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async showAddObjectiveDialog (period) {
      try {
        await this.axios.post('/api/pdp/objectives', {
          periodId: period.Period_ID,
          objectiveText: '',
          type: 'Main',
          means: '',
          status: 'Not Done'
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadObjectives(period.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Objective added' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    deleteObjective (objective, period) {
      this.currentObjectiveToDelete = { objective, period }
      this.showDeleteObjectiveDialog = true
    },
    async confirmDeleteObjective () {
      if (!this.currentObjectiveToDelete) return
      const { objective, period } = this.currentObjectiveToDelete
      try {
        await this.axios.delete(`/api/pdp/objectives/${objective.Objective_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadObjectives(period.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Objective deleted successfully' })
        this.showDeleteObjectiveDialog = false
        this.currentObjectiveToDelete = null
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    setNoteDraft (periodId, value) {
      this.$set(this.noteDrafts, periodId, value)
    },
    getCurrentPeriodNote (period) {
      if (!period || !period.Period_ID) return null
      const list = this.notes[period.Period_ID] || []
      if (!list.length) return null
      return list[0]
    },
    showNewNoteField (period) {
      if (!period || !period.Period_ID) return
      if (this.addingNotePeriodId === period.Period_ID) {
        this.$nextTick(() => {
          const ref = this.$refs['noteInput-' + period.Period_ID]
          const comp = Array.isArray(ref) ? ref[0] : ref
          if (comp && comp.$el) {
            const textarea = comp.$el.querySelector('textarea')
            if (textarea) textarea.focus()
          }
        })
        return
      }
      this.addingNotePeriodId = period.Period_ID
      const currentNote = this.getCurrentPeriodNote(period)
      this.$set(this.noteDrafts, period.Period_ID, currentNote ? (currentNote.Note_Text || '') : '')
      this.$nextTick(() => {
        const ref = this.$refs['noteInput-' + period.Period_ID]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.$el) {
          const textarea = comp.$el.querySelector('textarea')
          if (textarea) textarea.focus()
        }
      })
    },
    async onNoteBlur (period) {
      if (!period || !period.Period_ID) return
      const periodId = period.Period_ID
      if (this.noteSaveLocks[periodId]) return
      this.$set(this.noteSaveLocks, periodId, true)
      try {
        const text = typeof this.noteDrafts[periodId] === 'string' ? this.noteDrafts[periodId] : ''
        const currentNote = this.getCurrentPeriodNote(period)
        const currentText = (currentNote && currentNote.Note_Text) || ''
        this.addingNotePeriodId = null
        if (text === currentText) return
        await this.savePeriodNote(period, text)
      } finally {
        this.$set(this.noteSaveLocks, periodId, false)
      }
    },
    async savePeriodNote (period, noteText) {
      const text = typeof noteText === 'string' ? noteText : ''
      if (!period || !period.Period_ID) {
        this.$store.dispatch('setError', { errorMsg: 'Period must be created first' })
        return
      }

      try {
        await this.axios.post(`/api/pdp/periods/${period.Period_ID}/notes`, {
          noteText: text
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadPeriodNotes(period.Period_ID)
        this.$store.dispatch('setSuccess', { successMsg: 'Note saved successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    }
  }
}
</script>

<style scoped>
.pdp-table {
  background: var(--v-surface-base);
  border-radius: 4px;
}

.pdp-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.theme--dark .pdp-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.pdp-table >>> table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

.pdp-table >>> table thead th.column.text-start,
.pdp-table >>> table thead th.column.text-center,
.pdp-table >>> table thead th {
  box-sizing: border-box;
}

.pdp-table >>> td.objectives-td,
.pdp-table >>> th[role="columnheader"]:nth-child(1) {
  width: 300px;
}

.pdp-table >>> td.type-td,
.pdp-table >>> th[role="columnheader"]:nth-child(2) {
  width: 65px;
}

.pdp-table >>> td.means-td,
.pdp-table >>> th[role="columnheader"]:nth-child(3) {
  width: 270px;
}

.pdp-table >>> td.status-td,
.pdp-table >>> th[role="columnheader"]:nth-child(4) {
  width: 60px;
}

.pdp-table >>> table tbody td {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.theme--dark .pdp-table >>> table tbody td {
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
  background-color: var(--v-surface-base) !important;
  color: rgba(255, 255, 255, 0.87) !important;
}

.pdp-table >>> table tbody td.notes-td {
  padding: 4px !important;
  border-right: none !important;
}

.pdp-table >>> table thead th {
  border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .pdp-table >>> table thead th {
  border-right: 1px solid rgba(255, 255, 255, 0.12) !important;
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.87) !important;
}

.pdp-table >>> table thead th:last-child {
  border-right: none !important;
}

.pdp-table >>> tbody tr {
  background: transparent !important;
}

.pdp-table >>> tbody tr:hover {
  background: transparent !important;
}

.pdp-table >>> tbody tr:hover td {
  background-color: #eeeeee !important;
}

.theme--dark .pdp-table >>> tbody tr:hover td {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.pdp-table >>> tbody tr:hover td.meeting-date-td,
.pdp-table >>> tbody tr:hover td.period-td,
.pdp-table >>> tbody tr:hover td.progress-td,
.pdp-table >>> tbody tr:hover td.notes-td {
  background: transparent !important;
}

.table-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .table-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.objective-row td {
  padding: 2px !important;
  vertical-align: middle !important;
  text-align: center;
  height: auto !important;
}

.meeting-date-td,
.period-td,
.progress-td,
.notes-td {
  vertical-align: middle !important;
}

.meeting-date-td .meeting-date-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px 0;
}

.period-td .period-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  gap: 4px;
}

.period-text {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
}

.objectives-td {
  text-align: left;
  overflow: visible;
}

.objectives-cell--row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;
  width: 100%;
}

.objectives-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 2px 0;
  width: 100%;
}

.period-actions {
  display: flex;
  align-items: center;
}

.objectives-content-wrapper {
  flex: 1;
  min-width: 0;
}

.objectives-actions .action-icon {
  cursor: pointer;
  opacity: 0.6;
}

.objectives-actions .action-icon:hover {
  opacity: 1;
}

.objectives-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.drag-handle {
  flex-shrink: 0;
}

.objectives-actions-divider {
  width: 1px;
  height: 16px;
  background: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.theme--dark .objectives-actions-divider {
  background: rgba(255, 255, 255, 0.3);
}

.add-objective-btn {
  margin-left: 0;
}

.header-with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.header-text {
  display: block;
  width: 100%;
}

.header-text--left {
  text-align: left;
}

.header-text--center {
  text-align: center;
}

.theme--dark .pdp-table .header-text {
  color: rgba(255, 255, 255, 0.87);
}

.header-action-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
}

.type-cell,
.means-cell,
.status-cell {
  padding: 8px 0;
}

.notes-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notes-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
  overflow: hidden;
}

.objective-item {
  display: flex;
  align-items: baseline;
}

.objective-item:not(:last-child) {
  margin-bottom: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .objective-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.objective-text {
  flex: 1;
  font-size: 0.875rem;
}

.type-select,
.status-select {
  font-size: 0.875rem;
}

.type-select >>> .v-input__control,
.status-select >>> .v-input__control {
  min-height: 24px !important;
  height: 24px !important;
}

.type-select >>> .v-input__append-inner,
.status-select >>> .v-input__append-inner {
  padding: 0 !important;
}

.type-select >>> .v-input__slot,
.status-select >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 !important;
  min-height: 24px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.type-select >>> .v-input__slot::before,
.type-select >>> .v-input__slot::after,
.status-select >>> .v-input__slot::before,
.status-select >>> .v-input__slot::after {
  display: none !important;
}

.type-select >>> .v-select__selections,
.status-select >>> .v-select__selections {
  padding-left: 0;
}

.type-select >>> .v-select__selections .v-select__selection--comma,
.status-select >>> .v-select__selections .v-select__selection--comma {
  padding-left: 0;
}

.objective-input,
.means-input {
  font-size: 0.875rem;
}

.objective-input {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  min-height: 24px;
  margin: 0;
  padding: 2px 4px;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  color: inherit;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

.objective-input:disabled,
.objective-input[readonly] {
  color: inherit;
  opacity: 1;
}

.means-input >>> .v-input__control {
  min-height: 24px !important;
  height: auto !important;
}

.means-input >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 4px !important;
  min-height: 24px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.means-input >>> .v-input__slot::before,
.means-input >>> .v-input__slot::after {
  display: none !important;
}

.means-input >>> .v-text-field__slot input,
.means-input >>> textarea {
  padding: 0 !important;
}

.means-input >>> textarea {
  line-height: 1.5 !important;
  padding: 6px 0 !important;
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
}

.theme--dark .objective-input,
.theme--dark .means-input >>> textarea {
  color: rgba(255, 255, 255, 0.87) !important;
}

.theme--dark .notes-inline-input >>> textarea {
  color: rgba(255, 255, 255, 0.87) !important;
}

.type-td >>> .v-input,
.means-td >>> .v-input,
.status-td >>> .v-input {
  margin: 0 auto;
  max-width: 100%;
}

.means-td {
  text-align: left !important;
}

.means-td >>> .v-input {
  margin: 0 !important;
  width: 100%;
}

.means-link-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.means-value {
  min-width: 0;
  flex: 1;
  text-align: left;
}

.means-edit-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  width: 100%;
}

.means-edit-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.means-edit-row .means-hybrid {
  flex: 1;
  min-width: 0;
}

.means-hybrid {
  width: 100%;
}

.means-combined-textarea {
  width: 100%;
  box-sizing: border-box;
}

.means-links-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.means-hybrid-textarea {
  min-width: 48px;
  min-height: 28px;
  max-width: 100%;
  padding: 6px 8px;
  font-size: 0.875rem;
  line-height: 1.45;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  color: inherit;
  resize: none;
  overflow: hidden;
  font-family: inherit;
}

.theme--dark .means-hybrid-textarea {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.87);
}

.means-hybrid-textarea:focus {
  outline: none;
  border-color: #1976d2;
}

.means-hybrid-link-chip {
  max-width: 100%;
}

.means-hybrid-link-label {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.means-hybrid-link-edit {
  margin: 0 -4px 0 2px !important;
}

.means-hybrid-hint {
  margin-top: 6px;
  line-height: 1.35;
}

.means-link-btn {
  flex-shrink: 0;
  margin-top: 2px;
}

.means-link {
  color: #1976d2;
  text-decoration: underline;
  word-break: break-all;
}

.means-edit-icon {
  opacity: 1;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
}

.action-pencil-icon {
  color: #1976d2 !important;
  font-size: 18px !important;
}

.action-delete-icon {
  color: #e53935 !important;
  font-size: 18px !important;
}

.delete-icon-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  margin: 0 !important;
  flex-shrink: 0;
}

.means-text {
  white-space: pre-line;
  word-break: break-word;
  text-align: left;
  display: block;
}

.period-notes-row td {
  border-top: 0 !important;
}

.period-notes-td {
  padding: 4px 10px !important;
}

.period-notes-label {
  font-size: 12px;
  font-weight: 600;
  color: #60758a;
  margin-bottom: 6px;
}

.theme--dark .period-notes-label {
  color: rgba(255, 255, 255, 0.65);
}

.type-td >>> .v-input,
.status-td >>> .v-input {
  width: fit-content !important;
  min-width: fit-content !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

.type-td >>> .v-input__slot,
.type-td >>> .v-select__selections,
.status-td >>> .v-input__slot,
.status-td >>> .v-select__selections {
  display: flex !important;
  justify-content: center !important;
}

.type-td >>> .v-select__selections input,
.status-td >>> .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.type-select >>> .v-input__append-inner,
.status-select >>> .v-input__append-inner {
  display: none !important;
}

.type-select >>> .v-select__selection,
.status-select >>> .v-select__selection {
  width: auto;
  max-width: none;
  margin-right: 0 !important;
}

.dropdown-pill {
  min-height: 26px;
  padding: 0 7px 0 9px;
  border-radius: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: fit-content;
  max-width: none;
}

.dropdown-pill__text {
  color: #fff;
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
}

.dropdown-pill__icon {
  color: #fff !important;
  font-size: 14px !important;
  flex-shrink: 0;
}

.dropdown-pill--type-main {
  background: #3c78cf;
}

.dropdown-pill--type-additional {
  background: #fb8c00;
}

.dropdown-pill--type-empty {
  background: #9e9e9e;
}

.dropdown-pill--status-done {
  background: #43a047;
}

.dropdown-pill--status-in-progress {
  background: #fbc02d;
}

.dropdown-pill--status-not-done {
  background: #757575;
}

.dropdown-pill--status-not-applicable {
  background: #8e24aa;
}

.dropdown-pill--status-on-hold {
  background: #ef6c00;
}

.dropdown-pill--status-empty {
  background: #9e9e9e;
}

.progress-td .progress-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.progress-cell {
  padding: 2px 0;
}

.progress-value {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.progress-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
}

.theme--dark .progress-details {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.last-update {
  font-size: 0.75rem;
}

.progress-details .trophy-icons {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 2px;
}

.notes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
  min-width: 0;
  width: 100%;
}

.note-comment {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  padding: 2px 0;
  margin-right: 8px;
  margin-bottom: 4px;
  max-width: 100%;
  min-width: 0;
  white-space: pre-line;
  word-break: break-word;
}

.note-comment__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.note-comment--long {
  cursor: pointer;
}

.note-comment--long:hover {
  opacity: 0.85;
}

.note-comment__delete-btn {
  margin-left: 2px !important;
}

.full-note-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.no-notes {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.5);
}

.theme--dark .no-notes {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.6);
}

.add-note-btn {
  margin-left: 8px;
  flex-shrink: 0;
}

.notes-inline-input {
  margin-top: 0;
  font-size: 0.875rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.notes-inline-input >>> .v-input {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__control {
  min-height: 28px !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__slot {
  padding: 0 4px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__slot::before,
.notes-inline-input >>> .v-input__slot::after {
  display: none !important;
}

.notes-inline-input >>> textarea {
  font-size: 0.875rem !important;
  line-height: 1.35 !important;
  padding: 2px 0 !important;
  white-space: pre-line !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
}

.action-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 1;
}

.add-period-btn {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.pdp-table >>> tbody tr.objective-row--dragging {
  opacity: 0.6;
}

.pdp-table >>> tbody tr.objective-row--drag-over {
  background-color: rgba(var(--v-theme-primary-rgb, 25, 118, 210), 0.08);
}

.theme--dark .pdp-table >>> tbody tr.objective-row--drag-over {
  background-color: rgba(var(--v-theme-primary-rgb, 25, 118, 210), 0.12);
}

</style>
